/**
 * Which 401s are NOT an expired session.
 *
 * The response interceptor in client.ts treats a 401 as "the access
 * cookie expired": it refreshes once and REPLAYS the same request. That
 * is wrong for a 401 that means "what the user typed was wrong" — a
 * mistyped 2FA code (`invalid_code`) or password (`invalid_credentials`).
 * Replaying it re-submits the same wrong value, so every typo spends TWO
 * of the identity's five lockout attempts (wave-2 shared counter: 5 →
 * 15-minute lock that also blocks sign-in), and the replay's second 401
 * then bounces the user to /login.
 *
 * Neither code is ever sent for an expired session — the auth middleware
 * answers those with `unauthenticated` — so the interceptor must reject
 * them straight away and let the form show the error inline.
 *
 * Deliberately code-based, not URL-based: a POST /auth/totp/verify whose
 * access cookie expired while the user sat on the page gets
 * `unauthenticated` from the middleware (the code was never checked), and
 * refresh-then-replay is still the right answer there.
 *
 * Kept free of imports so `node --test` can run its unit test directly.
 */
export const CREDENTIAL_FAILURE_CODES: ReadonlySet<string> = new Set([
  "invalid_code",
  "invalid_credentials",
  // A refused passkey assertion (sign-in, 2FA, step-up). Its challenge is
  // single-use, so a refresh-then-replay could only fail again (400
  // invalid_challenge), and on /login a failed refresh would bounce away.
  "passkey_invalid",
]);

/** True when a 401 body's `error` code means "wrong credential or second factor". */
export function isCredentialFailure(code: unknown): boolean {
  return typeof code === "string" && CREDENTIAL_FAILURE_CODES.has(code);
}
